import { motion } from 'framer-motion';
import Link from 'next/link';

const CallToAction = () => {
  return (
    <section className="relative overflow-hidden py-24 bg-gradient-to-br from-soft-gold/15 via-white to-cream">
      <motion.div
        className="pointer-events-none absolute inset-y-0 left-[-12rem] w-[22rem] rounded-full bg-soft-gold/40 blur-3xl"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.9, 1.05, 0.9] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-24 right-[-10rem] h-[28rem] w-[28rem] rounded-full bg-amber-200/40 blur-[140px]"
        animate={{ opacity: [0.2, 0.35, 0.2], scale: [0.85, 1.08, 0.92] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-12 items-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-6">
            <motion.span
              className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-warm-brown"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Limited cohort • Invitations open
            </motion.span>

            <motion.h2
              className="text-4xl md:text-5xl font-heading font-bold text-warm-brown"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              Ready to join the next chapter of our Balinese house?
            </motion.h2>

            <motion.p
              className="text-lg text-warm-brown/70 max-w-2xl"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.18 }}
            >
              We curate each placement with intention. Share your family story, meet our current litters, and access concierge support designed for discerning first adopters.
            </motion.p>

            <motion.ul
              className="grid gap-4 sm:grid-cols-2"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.26, staggerChildren: 0.12 }}
            >
              {["Private temperament consultation", "Transparent health dossiers", "Bespoke homecoming kit", "Lifetime concierge access"].map((item) => (
                <motion.li
                  key={item}
                  className="flex items-start gap-3 text-sm text-warm-brown/75"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                >
                  <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-soft-gold/15 text-soft-gold">✓</span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </motion.ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href="/waiting-list"
                className="inline-flex items-center justify-center rounded-xl bg-soft-gold px-6 py-3 text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Join the Waiting List
              </motion.a>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-soft-gold px-6 py-3 text-base font-semibold text-soft-gold transition-colors duration-300 hover:bg-soft-gold hover:text-white"
              >
                Speak with our concierge
              </Link>
            </div>
          </div>

          <motion.div
            className="relative rounded-3xl border border-soft-gold/40 bg-white/80 p-8 shadow-[0_24px_60px_rgba(180,150,90,0.25)] backdrop-blur"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="mb-6 flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-soft-gold/15 text-soft-gold text-lg">★</span>
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-soft-gold">LINDY Admissions</p>
                <p className="text-warm-brown font-semibold">Next review window closes in 5 days</p>
              </div>
            </div>
            <p className="text-sm text-warm-brown/70 leading-relaxed">
              Share a short profile about your home, lifestyle, and desired timeline. Our team responds within 48 hours with a tailored roadmap and available pairings.
            </p>
            <div className="mt-6 grid gap-4">
              <div className="rounded-2xl bg-soft-gold/10 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-soft-gold">Upcoming Salons</p>
                <p className="text-sm text-warm-brown/80">Virtual meet-and-greet • Every Thursday</p>
              </div>
              <div className="rounded-2xl bg-soft-gold/10 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-soft-gold">Placement Pace</p>
                <p className="text-sm text-warm-brown/80">Limited to 6 families per quarter for intentional pairing</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CallToAction;
